import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';
import './ContactPopup.css';
import analyticsService from '../utils/analyticsService';

const ContactPopup = ({ isOpen, onClose }) => {
  const popupRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  // New: form state and helpers
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Delay actual closing to allow animation to complete
    setTimeout(() => {
      onClose();
    }, 180); // Slightly shorter than animation to ensure smooth transition
  }, [onClose]);

  // Handle opening and closing animations and reset on open
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setShouldRender(true);
      // Reset transient state when opening
      setErrors({});
      setIsSubmitting(false);
      setIsSubmitted(false);
      setSubmitError('');
    } else if (shouldRender) {
      // Start closing animation
      setIsClosing(true);
      // Remove component after animation completes
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleClose();
      }
    };

    // Add event listener when the popup is open
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up the event listener when component unmounts or popup closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // New: simple validation
  const validate = (data) => {
    const newErrors = {};
    if (!data.name.trim()) newErrors.name = 'Name is required';
    if (!data.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim());
      if (!emailOk) newErrors.email = 'Enter a valid email';
    }
    if (!data.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const v = validate(formData);
    setErrors(v);
    if (Object.keys(v).length) return;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Import authenticated API function dynamically
      const { submitAuthenticatedContact } = await import('../utils/authApi');
      
      const response = await submitAuthenticatedContact({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });

      if (response.status === 'sent') {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        // Track successful contact form submission
        analyticsService.trackContactFormSubmission(true);
      } else {
        setSubmitError(response.message || 'Message failed to send, try later');
        // Track failed contact form submission
        analyticsService.trackContactFormSubmission(false);
      }
    } catch (err) {
      console.error('Contact submit failed:', err);
      setSubmitError(err.message || 'Message failed to send, try later');
      // Track contact form error
      analyticsService.trackError('Contact form submission failed', 'contact_form');
      analyticsService.trackContactFormSubmission(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!shouldRender) return null;

  return (
    <div className={`contact-popup-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`contact-popup ${isClosing ? 'closing' : ''}`} ref={popupRef}>
        <div className="contact-popup-header">
          <h2>Contact</h2>
          <button className="close-button" onClick={handleClose} aria-label="Close contact form">
            <X size={20} />
          </button>
        </div>
        <div className="contact-popup-content">
          {!isSubmitted ? (
            <form className="contact-form" onSubmit={onSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="contact-name">Name</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={onChange}
                  placeholder="Your name"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'contact-name-error' : undefined}
                  autoComplete="name"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <span id="contact-name-error" className="field-error">{errors.name}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={onChange}
                  placeholder="you@example.com"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'contact-email-error' : undefined}
                  autoComplete="email"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <span id="contact-email-error" className="field-error">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={onChange}
                  placeholder="Write your message..."
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'contact-message-error' : undefined}
                  disabled={isSubmitting}
                />
                {errors.message && (
                  <span id="contact-message-error" className="field-error">{errors.message}</span>
                )}
              </div>

              <button type="submit" className="submit-button" disabled={isSubmitting} aria-live="polite">
                {isSubmitting && <span className="spinner" aria-hidden="true" />}
                <span>{isSubmitting ? 'Sending…' : 'Send'}</span>
                {isSubmitting && <span className="sr-only">Sending your message</span>}
              </button>
              {submitError && <p className="submit-error" role="alert">{submitError}</p>}
            </form>
          ) : (
            <div className="confirmation">
              <CheckCircle size={40} className="success-icon" aria-hidden="true" />
              <h3 className="confirmation-title">Message sent</h3>
              <p className="confirmation-text">Thanks for reaching out. I’ll get back to you soon.</p>
              <button className="submit-button" onClick={handleClose}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactPopup;